import { api } from "../api";
import { ProgressionTemplate, ProgressionTemplateFromApi, ProgressionTemplateRequest, ProgressionContainer, ProgressionContainerFromApi } from "./types";
import { toProgressionTemplateRequest, transformProgressionTemplates, transformProgressionTemplate } from "./utils";

const extendedApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllProgressionTemplates: builder.query<ProgressionTemplate[], void>({
      query: () => ({ url: "/progression-template", method: "GET" }),
      transformResponse: (response: ProgressionTemplateFromApi[]) =>
        transformProgressionTemplates(response),
      providesTags: ["Progressions"],
    }),
    createProgressionTemplate: builder.mutation<ProgressionTemplate, Omit<ProgressionTemplate, "id">>({
      query: (template) => ({
        url: "/progression-template",
        method: "POST",
        body: toProgressionTemplateRequest(template),
      }),
      transformResponse: (response: ProgressionTemplateFromApi) =>
        transformProgressionTemplate(response),
      invalidatesTags: ["Progressions"],
    }),
    updateProgressionTemplate: builder.mutation<ProgressionTemplate, ProgressionTemplate>({
      query: (template) => ({
        url: "/progression-template",
        method: "PUT",
        body: toProgressionTemplateRequest(template),
      }),
      transformResponse: (response: ProgressionTemplateFromApi) =>
        transformProgressionTemplate(response),
      invalidatesTags: ["Progressions"],
    }),
    deleteProgressionTemplate: builder.mutation<void, number>({
      query: (id) => ({
        url: `/progression-template/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Progressions"],
    }),
    // Container endpoints
    getAllProgressionContainers: builder.query<ProgressionContainer[], void>({
      query: () => ({ url: "/progression-container", method: "GET" }),
      transformResponse: (response: ProgressionContainerFromApi[]) =>
        response.map((c) => ({ id: c.id, name: c.name, color: c.color })),
      providesTags: ["ProgressionContainers"],
    }),
    createProgressionContainer: builder.mutation<ProgressionContainer, { name: string; color?: string }>({
      query: (container) => ({
        url: "/progression-container",
        method: "POST",
        body: container,
      }),
      transformResponse: (response: ProgressionContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
      }),
      invalidatesTags: ["ProgressionContainers"],
    }),
    updateProgressionContainer: builder.mutation<ProgressionContainer, ProgressionContainer>({
      query: (container) => ({
        url: "/progression-container",
        method: "PUT",
        body: container,
      }),
      transformResponse: (response: ProgressionContainerFromApi) => ({
        id: response.id,
        name: response.name,
        color: response.color,
      }),
      invalidatesTags: ["ProgressionContainers"],
    }),
    deleteProgressionContainer: builder.mutation<void, { id: number; deleteTemplates: boolean }>({
      query: ({ id, deleteTemplates }) => ({
        url: `/progression-container/${id}?deleteTemplates=${deleteTemplates}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProgressionContainers", "Progressions"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllProgressionTemplatesQuery,
  useCreateProgressionTemplateMutation,
  useUpdateProgressionTemplateMutation,
  useDeleteProgressionTemplateMutation,
  useGetAllProgressionContainersQuery,
  useCreateProgressionContainerMutation,
  useUpdateProgressionContainerMutation,
  useDeleteProgressionContainerMutation,
} = extendedApi;
